import React from 'react';
import { Box, Typography, Button, IconButton, Paper, Chip } from '@mui/material';
import { FiTrash2 } from 'react-icons/fi';
import { colors } from '../theme/modernTheme';

/**
 * Reusable bulk action toolbar component
 * Displays when items are selected and provides bulk actions
 *
 * @param {number} selectedCount - Number of selected items on current page
 * @param {Function} onClearSelection - Handler to clear selection
 * @param {Function} onDelete - Handler for bulk delete action
 * @param {boolean} canDelete - Whether user has delete permission
 * @param {string} itemName - Name of items (e.g., 'contacts', 'campaigns')
 * @param {boolean} showSelectAllPage - Whether to show "Select All from Page" button
 * @param {Function} onSelectAllPage - Handler for select all from page action
 * @param {boolean} isAllPageSelected - Whether all items on current page are selected
 */
export default function BulkActionToolbar({
  selectedCount,
  onClearSelection,
  onDelete,
  canDelete = true,
  itemName = 'items',
  showSelectAllPage = false,
  onSelectAllPage,
  isAllPageSelected = false,
}) {
  if (selectedCount === 0) return null;

  return (
    <Paper
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        mb: 1,
        p: 1,
        borderRadius: '4px',
        backgroundColor: colors.primary.main + '10',
        border: `1px solid ${colors.primary.main}30`,
        boxShadow: 'none',
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Chip
            label={`${selectedCount} ${itemName} selected`}
            sx={{
              backgroundColor: colors.primary.main,
              color: '#fff',
              fontWeight: 600,
              fontSize: '0.875rem',
              borderRadius: '4px',
            }}
          />

          <Button
            size="small"
            onClick={onClearSelection}
            sx={{
              textTransform: 'none',
              fontSize: '0.8rem',
              fontWeight: 500,
              p: '5px',
              color: colors.text.primary,
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
          >
            Clear Selection
          </Button>

          {showSelectAllPage && onSelectAllPage && (
            <Button
              size="small"
              onClick={onSelectAllPage}
              sx={{
                backgroundColor: 'transparent',
                textTransform: 'none',
                fontSize: '0.8rem',
                fontWeight: 500,
                p: '5px',
                color: colors.primary.main,
                '&:hover': {
                  backgroundColor: 'transparent',
                  pointer: 'cursor',
                },
              }}
            >
              {isAllPageSelected ? 'Deselect all on this page' : 'Select all on this page'}
            </Button>
          )}
        </Box>

        <Box display="flex" alignItems="center" gap={1}>
          {canDelete && (
            <IconButton
              size="small"
              onClick={onDelete}
              sx={{
                color: colors.text.primary,
                p: 1,
                '&:hover': {
                  backgroundColor: 'transparent',
                  color: colors.error.main,
                },
              }}
            >
              <FiTrash2 size={16} />
            </IconButton>
          )}
        </Box>
      </Box>
    </Paper>
  );
}
